import { Component, type ErrorInfo, type ReactNode } from "react";

import { ChunkReloadHint } from "@/components/ChunkReloadHint";
import { isChunkLoadError, requestChunkReload } from "@/lib/chunkReload";

// ---------------------------------------------------------------------------
// ChunkErrorBoundary — Suspense-nahe Boundary fuer lazy-Ladefehler (POL-1).
// ---------------------------------------------------------------------------
// Sitzt direkt um die lazy-Routen (siehe App.tsx). Faengt NUR
// Chunk-/Dynamic-Import-Ladefehler ab (stale Deploy) und zeigt den
// Reload-Hinweis + stoesst einen einmaligen, geguardeten Reload an.
//
// Echte Render-Fehler werden NICHT maskiert: sie werden in render() erneut
// geworfen und laufen so an die aeussere ErrorBoundary (Sentry + generische
// Meldung). „Ein Weg pro Sache" — die Chunk-Erkennung + Reload-Logik lebt
// zentral in lib/chunkReload.ts, hier nur die React-Grenze.

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      // Genau EIN kontrollierter Reload; der Guard verhindert die Schleife.
      requestChunkReload();
    }
    // Nicht-Chunk-Fehler NICHT hier melden — render() wirft sie erneut, die
    // aeussere ErrorBoundary uebernimmt Sentry + die generische Meldung.
  }

  render() {
    const { error } = this.state;
    if (error) {
      if (!isChunkLoadError(error)) {
        // Echter Render-Fehler: an die aeussere ErrorBoundary weiterreichen.
        throw error;
      }
      return <ChunkReloadHint />;
    }
    return this.props.children;
  }
}
